import React, { useState, useEffect } from "react";
import "./OrganizationInfoForm.css";
import bear_pointing from "../../../../images/Starting-Your-Fundraiser/bear-pointing.png";

import {
  PaymentMethod,
  PaymentMethodReturn,
} from "../../../../Interfaces/Fundraiser";
import { OrganizationReturn } from "../../../../Interfaces/Organization";
import {
  sanitizeEmailInput,
  sanitizeTextInput,
} from "../../../../CommonFunctions";
import { FundraiserInfoProps } from "../Step2-FundraiserInfo/FundraiserInfoForm";
import { useNavigate } from "react-router-dom";

import { stateAbbreviations } from "../../../../CommonFunctions";
import config from "../../../../config";
import apiClient from "../../../../services/apiClient";
import fetchWithAuth from "../../../../services/fetchWithAuth";

import { fetchFeatureFlags } from "../../../../services/fetchFeatureFlags";

const BASE_API = config.API_BASE_URL;

const OrganizationInfoForm = () => {
  const [formData, setFormData] = useState({
    organization: "",
    coordname: "",
    coordemail: "",
    color1: "#000000",
    color2: "#000000",
    payment: "",
    teamAdminName: "",
    primaryDivisionName: "",
    secondaryDivisionName: "",
    fullName: "",
    city: "",
    streetAddress: "",
    state: "",
    apt: "",
    zipcode: "",
  });

  const navigate = useNavigate();

  const [file, setFile] = useState<File | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationReturn[]>([]);

  const [flags, setFlags] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await fetchWithAuth(BASE_API + "organizations/all");
        const data = await response.json();
        setOrganizations(data);
      } catch (error) {
        console.error("Error fetching organizations:", error);
      }
    };

    fetchOrganizations();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === "organization") {
      const selectedOrg = organizations.find(
        (org) => org.id === parseInt(value, 10)
      );
      if (selectedOrg) {
        setFormData({
          ...formData,
          organization: value,
          primaryDivisionName: selectedOrg.local_name_for_primary_division,
          secondaryDivisionName: selectedOrg.local_name_for_secondary_division,
          teamAdminName: selectedOrg.local_name_for_team_admin,
        });
      } else {
        setFormData({
          ...formData,
          organization: value,
          primaryDivisionName: "",
          secondaryDivisionName: "",
          teamAdminName: "",
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };

  async function createPaymentMethod(): Promise<number> {
    const paymentMethod: PaymentMethod = {
      street_address: sanitizeTextInput(formData.streetAddress) || null,
      city: sanitizeTextInput(formData.city) || null,
      state: formData.state || null,
      zipcode: sanitizeTextInput(formData.zipcode) || null,
      payment_type: formData.payment === "check" ? "check" : "dir_dep",
      payable_to_name: sanitizeTextInput(formData.fullName) || null,
    };

    const response = await apiClient.post<PaymentMethodReturn>(
      BASE_API + "fundraiser/payment_method",
      paymentMethod,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data.id;
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formDataToSubmit = new FormData();
    for (const key in formData) {
      if (Object.prototype.hasOwnProperty.call(formData, key)) {
        formDataToSubmit.append(key, formData[key as keyof typeof formData]);
      }
    }
    if (file) {
      formDataToSubmit.append("file", file);
    }

    // Create the PaymentMethod Object (Static for now)
    const paymentMethodID = await createPaymentMethod();

    const unChangedOrgColors: boolean = formData.color1 === "#000000" && formData.color2 === "#000000";

    const fundraiserInfo: FundraiserInfoProps = {
      organization_id: parseInt(formData.organization, 10),
      coordinator_name: sanitizeTextInput(formData.coordname),
      coordinator_email: sanitizeEmailInput(formData.coordemail),
      file_location_ref: null,
      primary_color: unChangedOrgColors ? "" : formData.color1,
      secondary_color: unChangedOrgColors ? "" : formData.color2,
      payment_method_id: paymentMethodID,
      teamAdminName: formData.teamAdminName,
      primaryDivisionName: formData.primaryDivisionName,
      secondaryDivisionName: formData.secondaryDivisionName,
    };

    navigate("/create-fundraiser/fundraiser-info", { state: fundraiserInfo });
  };

  useEffect(() => {
    const loadFeatureFlags = async () => {
      const flagData = await fetchFeatureFlags();
      setFlags(flagData);
    };

    loadFeatureFlags();
  }, []);
  return (
    <div className="form-wrapper">
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <label className="input-label" htmlFor="organization">
              Select Organization *
            </label>
            <select
              id="organization"
              name="organization"
              value={formData.organization}
              onChange={handleChange}
              required
            >
              <option value="" disabled>
                Select an organization
              </option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.org_display_name}
                </option>
              ))}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label" htmlFor="coordname">
              Fundraiser Coordinator Name *
            </label>
            <input
              type="text"
              id="coordname"
              name="coordname"
              value={formData.coordname}
              onChange={handleChange}
              placeholder="Coordinator Full Name"
              required
            />
          </div>
          <div className="input-group">
            <label className="input-label" htmlFor="coordemail">
              Fundraiser Coordinator Email *
            </label>
            <input
              type="email"
              id="coordemail"
              name="coordemail"
              value={formData.coordemail}
              onChange={handleChange}
              placeholder="Coordinator Email"
              required
            />
          </div>
          <div className="input-group">
            <label className="input-label">Organization Colors</label>
            <div className="color-inputs">
              <div className="color-input">
                <input
                  type="color"
                  id="color1"
                  name="color1"
                  value={formData.color1}
                  onChange={handleChange}
                  required
                />
                <label className="color-label" htmlFor="color1">
                  Primary
                </label>
              </div>
              <div className="color-input">
                <input
                  type="color"
                  id="color2"
                  name="color2"
                  value={formData.color2}
                  onChange={handleChange}
                  required
                />
                <label className="color-label" htmlFor="color2">
                  Secondary
                </label>
              </div>
            </div>
          </div>
          {flags.organization_logo && (
            <div className="input-group">
              <label className="input-label" htmlFor="file">
                Organization Logo
              </label>
              <div className="file-input-wrapper">
                <span className="file-name">
                  {file ? file.name : "No file chosen"}
                </span>
                <input
                  type="file"
                  id="file"
                  name="file"
                  onChange={handleFileChange}
                />
                <label className="file-label" htmlFor="file">
                  Upload
                </label>
              </div>
            </div>
          )}
          <div className="input-group">
            <label className="input-label" htmlFor="payment">
              Payment *
            </label>
            <select
              id="payment"
              name="payment"
              value={formData.payment}
              onChange={handleChange}
              required
            >
              <option value="">How would you like to do your payment?</option>
              <option value="check">Check</option>
              <option value="direct_deposit">Direct Deposit</option>
            </select>
            <p className="payment-help-text">
              *If DD ask for bank account info.
            </p>
            <p className="payment-help-text">
              *If check ask for address and check payable name.
            </p>
          </div>
          {formData.payment === "check" && (
            <div className="payment-details">
              <h3>Check Payment Details</h3>
              <div className="grid-container">
                <div className="grid-item">
                  <label htmlFor="fullName">Full Name*</label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Full Name"
                    required
                  />
                </div>
                <div className="grid-item">
                  <label htmlFor="city">City*</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                    required
                  />
                </div>
                <div className="grid-item">
                  <label htmlFor="streetAddress">Street Address*</label>
                  <input
                    type="text"
                    id="streetAddress"
                    name="streetAddress"
                    value={formData.streetAddress}
                    onChange={handleChange}
                    placeholder="Street Address"
                    required
                  />
                </div>
                <div className="grid-item">
                  <label className="input-label" htmlFor="state">
                    State*
                  </label>
                  <select
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a state</option>
                    {stateAbbreviations.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid-item">
                  <label htmlFor="apt">Apt</label>
                  <input
                    type="text"
                    id="apt"
                    name="apt"
                    value={formData.apt}
                    onChange={handleChange}
                    placeholder="Apt"
                  />
                </div>
                <div className="grid-item">
                  <label htmlFor="zipcode">Zipcode*</label>
                  <input
                    type="text"
                    id="zipcode"
                    name="zipcode"
                    value={formData.zipcode}
                    onChange={handleChange}
                    placeholder="Zipcode"
                    required
                  />
                </div>
              </div>
            </div>
          )}
          <div className="submit-container">
            <img
              src={bear_pointing}
              alt="bear-pointing"
              className="centered-image"
            />
            <input type="submit" value="Submit" className="submit-button" />
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrganizationInfoForm;
